import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "src/store";
import { adaptGenaiUsage } from "../governance/adapter";
import { deriveDisplayEntries, deriveDisplayState } from "../governance/selectors";
import { BlockedFrom429, DisplayEntry, DisplayState, GovernanceSummary } from "../governance/types";
import { IGenai429Detail, IGenaiUsageData } from "../models/interfaces";

interface GovernanceState {
	summary: GovernanceSummary | null;
	displayState: DisplayState;
	isFetching: boolean;
	lastFetchedAt: number | null;
	/** ISO timestamp of the next scheduled refetch after a block window resets. */
	nextRecheckAt: string | null;
	/** Set by the 429 path to override the derived displayState until the next fetch. */
	blockOverride: BlockedFrom429 | null;
}

export const initialState: GovernanceState = {
	summary: null,
	displayState: { kind: "hidden" },
	isFetching: false,
	lastFetchedAt: null,
	nextRecheckAt: null,
	blockOverride: null,
};

const recomputeDisplayState = (state: GovernanceState): DisplayState => {
	if (state.blockOverride) {
		return {
			kind: "blocked",
			window: state.blockOverride.window,
			resetsAt: state.blockOverride.resetsAt,
		};
	}
	return deriveDisplayState(state.summary);
};

const governanceSlice = createSlice({
	name: "governanceSlice",
	initialState,
	reducers: {
		setFetching: (state, action: PayloadAction<boolean>) => {
			state.isFetching = action.payload;
		},
		setSummary: (state, action: PayloadAction<IGenaiUsageData | null>) => {
			state.summary = action.payload ? adaptGenaiUsage(action.payload) : null;
			state.lastFetchedAt = Date.now();
			// A fresh fetch supersedes the optimistic 429 override.
			state.blockOverride = null;
			state.displayState = recomputeDisplayState(state);
			state.nextRecheckAt = state.displayState.kind === "blocked" ? state.displayState.resetsAt : null;
		},
		setBlockedFrom429: (state, action: PayloadAction<IGenai429Detail>) => {
			const d = action.payload;
			state.blockOverride = {
				window: d.window,
				resetsAt: d.resets_at,
				message: typeof d.message === "string" ? d.message : undefined,
				used: typeof d.used === "number" ? d.used : undefined,
				limit: typeof d.limit === "number" ? d.limit : undefined,
				// Default to "dollars" for back-compat with unmigrated deploys that
				// don't yet emit `metric` on the 429 detail.
				metric: d.metric ?? "dollars",
			};
			state.displayState = recomputeDisplayState(state);
			state.nextRecheckAt = d.resets_at;
		},
		clearBlockOverride: (state) => {
			state.blockOverride = null;
			state.displayState = recomputeDisplayState(state);
			state.nextRecheckAt = state.displayState.kind === "blocked" ? state.displayState.resetsAt : null;
		},
		resetGovernance: () => initialState,
	},
});

export const { setFetching, setSummary, setBlockedFrom429, clearBlockOverride, resetGovernance } = governanceSlice.actions;

export const selectGovernanceState = (state: RootState) => state.governanceSlice;

export const selectDisplayState = createSelector(selectGovernanceState, (g) => g.displayState);

export const selectShouldDisableInput = createSelector(selectDisplayState, (ds) => ds.kind === "blocked");

/**
 * Per-window rows for the QuotaChip hover panel. Starts from the BE-derived
 * entries, then folds in the optimistic 429 `blockOverride`: the matching
 * window is forced to blocked/100%; if no window matches, a blocked row is
 * appended so the panel still reflects the active block.
 */
export const selectDisplayEntries = createSelector(selectGovernanceState, (g): DisplayEntry[] => {
	const base = deriveDisplayEntries(g.summary);
	const override = g.blockOverride;
	if (!override) return base;

	const matchIndex = base.findIndex((e) => e.window === override.window);
	if (matchIndex === -1) {
		return [...base, { window: override.window, status: "blocked", pctUsed: 100, resetsAt: override.resetsAt }];
	}

	return base.map((e, i) => (i === matchIndex ? { ...e, status: "blocked", pctUsed: 100 } : e));
});

export const selectWarningThresholdPct = createSelector(selectGovernanceState, (g) => g.summary?.warningThresholdPct ?? 0);

export const selectGovernanceMode = createSelector(selectGovernanceState, (g) => g.summary?.mode ?? "off");

export const selectGovernanceSummary = createSelector(selectGovernanceState, (g) => g.summary);

/**
 * BE-authored block context (message, used/limit). Only populated while a 429-driven
 * `blockOverride` is active. Returns null once the next /masters/genai-usage refresh clears it.
 */
export const selectBlockContext = createSelector(selectGovernanceState, (g) => g.blockOverride);

export default governanceSlice.reducer;
